import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { CountryFlag, Grid, Typography } from '@esgian/esgianui';
import { ProfileLink } from '@components/Links';
import { formatNumberReadable, sortByName } from '@helpers';
import { useSelector } from 'react-redux';
import { getUser } from '@store/features';
import TableV2 from '@components/Tables/TableV2';

function VesselsTable({ title, loading, vessels, hideColumns, fileName }) {
  const user = useSelector(getUser);
  const hasCountryShipyard = vessels?.some((v) => v?.countryShipyard !== undefined);

  const vesselTableData = useMemo(() => {
    if (!vessels?.length) return [];
    return vessels.map((item) => {
      let vesselSearchName = '';
      if (item.vesselName) {
        vesselSearchName = item.vesselName;
      }
      if (item.formerVesselName) {
        vesselSearchName = item.formerVesselName;
      }
      if (vesselSearchName === '') {
        vesselSearchName = null;
      }

      return {
        ...item,
        countryShipyard: {
          name: item.countryShipyard ?? null,
          isoAlpha2: item.countryShipyardISO2 ?? null,
          id: item.countryShipyardId ?? null
        },
        vessel: {
          name: item.vesselName,
          searchName: vesselSearchName,
          id: item.imo,
          vesselOldNames: item.formerVesselName
        },
        owner: {
          name: item.ownerLongName,
          id: item.ownerId
        },
        operator: {
          name: item.operatorShortName,
          id: item.operatorId
        }
      };
    });
  }, [vessels]);

  const columns = useMemo(() => {
    return [
      {
        name: 'vessel',
        label: 'Vessel Name',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customFilterType: 'multiselect',
          sortCompare: sortByName,
          setCellProps: () => {
            return {
              style: {
                boxShadow: '3px 0px 2px -1px rgb(0 0 0 / 20%)'
              }
            };
          },
          customDownloadValue: (value) => {
            return value?.name ?? '-';
          },
          customBodyRender: (value) => {
            return value !== null ? (
              <ProfileLink
                profile={'vessel'}
                id={value.id}
                name={value.name}
                vesselOldNames={value.vesselOldNames ?? value.name}
              />
            ) : (
              '-'
            );
          }
        }
      },
      {
        name: 'imo',
        label: 'IMO Number',
        options: {
          display: !hideColumns.includes('imo'),
          download: !hideColumns.includes('imo'),
          filter: !hideColumns.includes('imo'),
          customFilterType: 'textField',
          sort: true,
          sortThirdClickReset: true
        }
      },
      {
        name: 'mmsi',
        label: 'MMSI',
        options: {
          display: !hideColumns.includes('mmsi'),
          download: !hideColumns.includes('mmsi'),
          filter: !hideColumns.includes('mmsi'),
          sort: true,
          sortThirdClickReset: true,
          customFilterType: 'textField'
        }
      },
      {
        name: 'callSign',
        label: 'Call Sign',
        options: {
          customFilterType: 'textField',
          sort: true,
          display: !hideColumns.includes('callSign'),
          download: !hideColumns.includes('callSign'),
          filter: !hideColumns.includes('callSign'),
          sortThirdClickReset: true,
          customBodyRender: (dataValue) => {
            return dataValue === '' ? '-' : dataValue;
          }
        }
      },
      {
        name: 'owner',
        label: 'Owner',
        options: {
          sort: true,
          display: !hideColumns.includes('ownerLongName'),
          download: !hideColumns.includes('ownerLongName'),
          filter: !hideColumns.includes('ownerLongName'),
          customFilterType: 'multiselect',
          sortThirdClickReset: true,
          sortCompare: sortByName,
          customDownloadValue: (value) => {
            return value?.name ?? '-';
          },
          customBodyRender: (value) => {
            return value.id !== null ? (
              <ProfileLink profile={'owner'} id={value.id} name={value.name} />
            ) : (
              '--'
            );
          }
        }
      },
      {
        name: 'operator',
        label: 'Operator',
        options: {
          display: !hideColumns.includes('operatorShortName'),
          download: !hideColumns.includes('operatorShortName'),
          filter: !hideColumns.includes('operatorShortName'),
          sort: true,
          customFilterType: 'textField',
          sortThirdClickReset: true,
          sortCompare: sortByName,
          customDownloadValue: (value) => {
            return value?.name ?? '-';
          },
          customBodyRender: (value) => {
            return value.id ? (
              <ProfileLink profile={'operator'} id={value.id} name={value.name} />
            ) : value.name ? (
              value.name
            ) : (
              '--'
            );
          }
        }
      },
      {
        name: 'class',
        label: 'Class',
        options: {
          display: !hideColumns.includes('class'),
          download: !hideColumns.includes('class'),
          filter: !hideColumns.includes('class'),
          customFilterType: 'multiselect',
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (dataValue) => {
            return dataValue === null ? '-' : dataValue;
          }
        }
      },
      {
        name: 'buildYear',
        label: 'Build Year',
        options: {
          display: !hideColumns.includes('buildYear'),
          download: !hideColumns.includes('buildYear'),
          filter: !hideColumns.includes('buildYear'),
          sort: true,
          sortThirdClickReset: true,
          customFilterType: 'minMax',
          customBodyRender: (dataValue) => {
            return dataValue === null ? '-' : dataValue;
          }
        }
      },
      {
        name: 'countryShipyard',
        filterKey: 'country.name',
        label: 'Country of build',
        options: {
          display: hasCountryShipyard && !hideColumns.includes('countryShipyard'),
          download: hasCountryShipyard && !hideColumns.includes('countryShipyard'),
          filter: hasCountryShipyard && !hideColumns.includes('countryShipyard'),
          sort: true,
          sortThirdClickReset: true,
          customFilterType: 'multiselect',
          sortCompare: sortByName,
          customDownloadValue: (value) => {
            return value?.name ?? '-';
          },
          customBodyRender: (dataValue) => {
            return dataValue.id !== null ? (
              <Grid container spacing={1}>
                <Grid item sx={{ placeSelf: 'flex-end' }}>
                  <CountryFlag size={20} countryCode={dataValue.isoAlpha2} />
                </Grid>
                <Grid item>
                  <ProfileLink name={dataValue.name} profile={'country'} id={dataValue.id} />
                </Grid>
              </Grid>
            ) : (
              '--'
            );
          }
        }
      },
      {
        name: 'flagState',
        label: 'Flag State',
        options: {
          display: !hideColumns.includes('flagState'),
          download: !hideColumns.includes('flagState'),
          filter: !hideColumns.includes('flagState'),
          customFilterType: 'multiselect',
          sort: true,
          sortThirdClickReset: true
        }
      },
      {
        name: 'grossTonnage',
        label: 'GT (GrossTonnage)',
        options: {
          display: !hideColumns.includes('grossTonnage'),
          download: !hideColumns.includes('grossTonnage'),
          filter: !hideColumns.includes('grossTonnage'),
          sort: true,
          customFilterType: 'minMax',
          customBodyRender: (dataValue) => {
            return formatNumberReadable(dataValue?.toString());
          },
          sortThirdClickReset: true
        }
      },
      {
        name: 'deadweight',
        label: 'DWT (Deadweight)',
        options: {
          display: !hideColumns.includes('deadweight'),
          download: !hideColumns.includes('deadweight'),
          filter: !hideColumns.includes('deadweight'),
          sort: true,
          customFilterType: 'minMax',
          customBodyRender: (dataValue) => {
            return formatNumberReadable(dataValue?.toString());
          },
          sortThirdClickReset: true
        }
      },
      {
        name: 'loadCapacity',
        label: 'Load Capacity(CEU)',
        options: {
          display: !hideColumns.includes('loadCapacity'),
          download: !hideColumns.includes('loadCapacity'),
          filter: !hideColumns.includes('loadCapacity'),
          sort: true,
          sortThirdClickReset: true,
          customFilterType: 'minMax',
          customBodyRender: (dataValue) => {
            return formatNumberReadable(dataValue?.toString());
          }
        }
      },
      {
        name: 'maxLiftingCapacity',
        label: 'Max Lifting capacity',
        options: {
          display: !hideColumns.includes('maxLiftingCapacity'),
          download: !hideColumns.includes('maxLiftingCapacity'),
          filter: !hideColumns.includes('maxLiftingCapacity'),
          sort: true,
          customFilterType: 'minMax',
          sortThirdClickReset: true,
          customBodyRender: (dataValue) => {
            const formattedData =
              typeof dataValue === 'number' ? formatNumberReadable(dataValue) : dataValue;
            return formattedData === null ? '-' : formattedData;
          }
        }
      },
      {
        name: 'numCranes',
        label: 'No. Cranes',
        options: {
          display: !hideColumns.includes('numCranes'),
          download: !hideColumns.includes('numCranes'),
          filter: !hideColumns.includes('numCranes'),
          sort: true,
          customFilterType: 'minMax',
          sortThirdClickReset: true,
          customBodyRender: (dataValue) => {
            return dataValue === null ? '-' : dataValue;
          }
        }
      }
    ];
  }, [hideColumns]);

  const options = useMemo(() => {
    return {
      onDownload: (buildHead, buildBody, columns, data) => {
        return (
          '\uFEFF' +
          buildHead(columns) +
          buildBody(
            data.map((item) => {
              item.data = item.data?.map((val) => {
                if (val === '-') {
                  return null;
                }
                return val;
              });
              return item;
            })
          )
        );
      },
      selectableRows: 'none',
      responsive: 'standard',
      filter: false,
      search: false,
      download: false,
      filename: fileName,
      setTableProps: () => {
        return {
          id: 'vessel-table'
        };
      },
      elevation: 0,
      print: false,
      viewColumns: false,
      pagination: true,
      toolbar: true,
      tableBodyMaxHeight: '80vh',
      rowsPerPageOptions: [10, 25, 50, 100],
      sortOrder: {
        name: 'vesselName',
        direction: 'asc'
      },
      textLabels: {
        body: {
          noMatch: 'Sorry, no vessels found'
        }
      }
    };
  }, [user, fileName]);

  return (
    <TableV2
      data={vesselTableData?.length ? vesselTableData : []}
      columns={columns}
      options={options}
      loading={loading}
      title={
        <Typography id={'vessel-title'} variant={'h6'}>
          {title}
        </Typography>
      }
    />
  );
}

VesselsTable.propTypes = {
  title: PropTypes.string,
  vessels: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  hideColumns: PropTypes.arrayOf(PropTypes.string),
  fileName: PropTypes.string.isRequired
};

VesselsTable.defaultProps = {
  title: '',
  vessel: [],
  loading: false,
  hideColumns: []
};

export default VesselsTable;
